import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiZap, FiTrendingUp, FiShield, FiGlobe, FiUsers, FiBarChart3, FiArrowRight} = FiIcons;

const LandingPage = () => {
  const features = [
    {
      icon: FiTrendingUp,
      title: 'Boost Conversions',
      description: 'Increase sales by showing real-time purchase notifications to build trust and urgency.'
    },
    {
      icon: FiShield,
      title: 'Safe & Secure',
      description: 'Enterprise-grade security with domain validation and rate limiting protection.'
    },
    {
      icon: FiGlobe,
      title: 'Easy Integration',
      description: 'Simple one-line script integration. Works with any website or platform.'
    },
    {
      icon: FiUsers,
      title: 'Social Proof',
      description: 'Leverage psychological triggers to build credibility and encourage purchases.'
    },
    {
      icon: FiBarChart3,
      title: 'Analytics',
      description: 'Track impressions and performance with detailed analytics dashboard.'
    },
    {
      icon: FiZap,
      title: 'Lightning Fast',
      description: 'Ultra-fast loading with CDN delivery and optimized performance.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Boost Sales with{' '}
              <span className="text-blue-600 block">Viral Social Proof</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Create authentic purchase notifications that build trust, 
              create urgency, and increase conversions on your website. 
              <span className="font-semibold text-blue-600"> Completely free forever.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/signup"
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg"
              >
                Sign Up Free
                <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
              </Link>
              <Link 
                to="#demo"
                className="inline-flex items-center px-8 py-4 border border-gray-300 text-lg font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                View Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose ViralToast?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to create compelling social proof notifications that convert visitors into customers.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{opacity: 0, y: 30}}
                whileInView={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                viewport={{once: true}}
                className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-lg mb-6">
                  <SafeIcon icon={feature.icon} className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section id="demo" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Watch how social proof notifications appear on your website
            </p>
          </motion.div>
          
          <div className="relative max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-2xl p-8 relative overflow-hidden">
              <div className="text-center py-20">
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  Your Website Content
                </h3>
                <p className="text-gray-600 mb-8">
                  Social proof notifications will appear here automatically
                </p>
                <div className="bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg p-8">
                  <SafeIcon icon={FiZap} className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                  <p className="text-gray-700">
                    The live social proof notifications are now active on this page!
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Look for notifications appearing in the bottom-left corner
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
            className="text-center"
          >
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Boost Your Conversions?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join thousands of businesses using ViralToast to increase their sales with authentic social proof notifications. 
              <span className="block mt-2 font-semibold">No credit card required. Forever free.</span>
            </p>
            <Link 
              to="/signup"
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-blue-600 bg-white hover:bg-gray-50 transition-all transform hover:scale-105 shadow-lg"
            >
              Sign Up Free
              <SafeIcon icon={FiArrowRight} className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;